"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, Sparkles, ArrowRight, FlaskConical, CircleCheck as CheckCircle2, Leaf, Layers, ChevronRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const features = [{
  id: 1,
  title: "Raw Material Verification",
  desc: "Rapid identification of botanicals, actives, and excipients before batch production."
}, {
  id: 2,
  title: "Safety & Contaminant Testing",
  desc: "Sensitive detection of trace impurities, heavy metals, and residual solvents."
}, {
  id: 3,
  title: "Formulation Stability",
  desc: "Application-led analytical systems to monitor shelf-life and active ingredient potency."
}];
export default function NutraceuticalHero() {
  const shouldReduceMotion = useReducedMotion();
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="nutraceuticalcosmetichero" data-nav="light" className="relative w-full overflow-hidden bg-background py-16 sm:py-20 lg:py-28">
      {/* Subtle organic background accent blurs */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-24 left-1/2 -z-10 h-96 w-full -translate-x-1/2 max-w-7xl opacity-40 blur-3xl">
        <div className="h-full w-full bg-linear-to-tr from-accent/50 via-secondary to-muted" />
      </div>

      <div className="page-container">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-8">
          
          {/* Text Content Column */}
          <div className="flex flex-col items-start lg:col-span-7">
            
            {/* Breadcrumb Navigation */}
            <nav aria-label="Breadcrumb" className="mb-6">
              <ol className="flex flex-wrap items-center gap-1.5 text-xs sm:text-sm text-muted-foreground">
                <li className="inline-flex items-center gap-1.5">
                  <Link to="/HomePage" className="transition-colors hover:text-foreground">
                    Home
                  </Link>
                </li>
                <li aria-hidden="true" className="text-muted-foreground/60">
                  <ChevronRight className="size-3.5" />
                </li>
                <li className="inline-flex items-center gap-1.5">
                  <Link to="/IndustriesPage" className="transition-colors hover:text-foreground">
                    Industries
                  </Link>
                </li>
                <li aria-hidden="true" className="text-muted-foreground/60">
                  <ChevronRight className="size-3.5" />
                </li>
                <li className="inline-flex items-center gap-1.5">
                  <span className="font-medium text-foreground" aria-current="page">
                    Nutraceutical and Cosmetic
                  </span>
                </li>
              </ol>
            </nav>

            {/* Pill Badge */}
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary/80 px-3.5 py-1 text-xs font-medium text-secondary-foreground shadow-2xs backdrop-blur-xs">
              <Leaf className="size-3.5 text-primary" />
              <span>Nutraceutical &amp; Cosmetic Quality Assurance</span>
            </div>

            {/* Main Headline */}
            <h1 className="mt-5 text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-6xl lg:leading-[1.12]">
              Precision analytics for safe, pure health and beauty products.
            </h1>

            {/* Supporting Copy */}
            <p className="mt-6 text-base text-muted-foreground sm:text-lg lg:max-w-xl">
              Antech Solutions delivers qualification-ready instrumentation, rapid raw material identification, and lifecycle support to cosmetics and dietary supplement labs across Ireland and the UK.
            </p>

            {/* Action Buttons */}
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="rounded-md font-medium shadow-sm">
                <Link to="/SolutionsPage">
                  Explore Solutions
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="rounded-md font-medium">
                <Link to="/ContactPage">
                  Speak with a Specialist
                </Link>
              </Button>
            </div>

            {/* Trust points */}
            <div className="mt-10 grid w-full grid-cols-1 gap-3 border-t border-border pt-8 sm:grid-cols-2">
              <div className="flex items-center gap-2.5 text-sm font-medium text-foreground">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>EP, USP &amp; ISO Compliance Support</span>
              </div>
              <div className="flex items-center gap-2.5 text-sm font-medium text-foreground">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>Dedicated UK &amp; Ireland Field Engineers</span>
              </div>
            </div>
          </div>

          {/* Media and Feature Highlights Column */}
          <div className="relative lg:col-span-5">
            <div className="relative mx-auto w-full max-w-lg lg:max-w-none">
              
              {/* Main Image Surface */}
              <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-xl">
                <div className="relative aspect-4/3 w-full sm:aspect-5/4">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/nutraceutical_cosmetic_lab_testing.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/nutraceutical_cosmetic_lab_testing.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/nutraceutical_cosmetic_lab_testing.webp-wvc-sizes" alt="Analytical laboratory formulation testing for cosmetics and botanicals" loading="lazy" decoding="async" className="h-full w-full object-cover object-center" />
                  <div className="absolute inset-0 bg-linear-to-t from-foreground/40 via-transparent to-transparent" />
                  
                  {/* Floating Metric Badge on Image */}
                  <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between rounded-xl border border-border/20 bg-background/90 p-3 shadow-lg backdrop-blur-md">
                    <div className="flex items-center gap-3">
                      <div className="flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <FlaskConical className="size-5" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-foreground">Batch Purity Index</p>
                        <p className="text-xs text-muted-foreground">Certified analytical verification</p>
                      </div>
                    </div>
                    <Badge variant="secondary" className="font-semibold text-foreground">
                      Reliable Performance
                    </Badge>
                  </div>
                </div>

                {/* Sub-cards / Feature List */}
                <div className="space-y-3 p-5 bg-card sm:p-6">
                  {features.map((feature, i) => <div key={feature.id} data-index={i} className="group flex items-start gap-3 rounded-lg border border-border/60 bg-muted/30 p-3.5 transition-[background-color,border-color] duration-200 hover:border-primary/40 hover:bg-muted/60">
                      <div className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-secondary text-primary">
                        {feature.id === 1 && <Layers className="size-3.5" />}
                        {feature.id === 2 && <ShieldCheck className="size-3.5" />}
                        {feature.id === 3 && <Sparkles className="size-3.5" />}
                      </div>
                      <div>
                        <h2 className="text-sm font-semibold text-foreground">
                          {feature.title}
                        </h2>
                        <p className="mt-0.5 text-xs text-muted-foreground leading-relaxed">
                          {feature.desc}
                        </p>
                      </div>
                    </div>)}
                </div>

              </div>

              {/* Decorative side accent badge */}
              <div className="absolute -bottom-5 -right-4 hidden rounded-xl border border-border bg-card p-4 shadow-lg sm:flex items-center gap-3">
                <div className="flex size-8 items-center justify-center rounded-full bg-primary text-primary-foreground">
                  <ShieldCheck className="size-4" />
                </div>
                <div>
                  <p className="text-xs font-bold text-foreground">ISO Qualified</p>
                  <p className="text-[11px] text-muted-foreground">Regulated Lab Ready</p>
                </div>
              </div>

            </div>
          </div>

        </div>
      </div>
    </section>;
}